// Bank rekvizitlari — `xsib-hr` servisining `bank/` qismi.
//
//   GET    /api/hr/persons/{id}/bank-accounts    hr.bank.view
//   POST   /api/hr/persons/{id}/bank-accounts    hr.bank.write
//
// =====================================================================
// ⚠️ 1-QOIDA — `PUT` HAM, `DELETE` HAM YO'Q
// =====================================================================
// Backendda ular umuman mavjud emas (`BankAccountController` da faqat
// `GET` va `POST`), bazada esa `hr.temporal_row_immutable()` yopilgan
// davrni o'zgartirishga yo'l qo'ymaydi.
//
// Panel tomonida oqibati aniq: tarix qatorida TAHRIRLASH va O'CHIRISH
// tugmasi CHIZILMAYDI. Rekvizit o'zgarsa — YANGI davr ochiladi, eskisi
// yopiladi. «Bu maosh qaysi hisobga ketgan edi» degan savol shu bilan
// javobsiz qolmaydi.
//
// =====================================================================
// ⚠️ 2-QOIDA — HISOB RAQAMI NIQOBLANMAYDI
// =====================================================================
// Server raqamni to'liq beradi va panel ham to'liq ko'rsatadi. Sabab
// abonent telefoni bilan bir xil (§26): niqob operatorning ishini
// bajarishga to'sqinlik qiladi — u aynan raqamni bankka o'tkazadi va
// uni harfma-harf solishtiradi. Himoya niqobda emas, `hr.bank.view`
// huquqida: u hech bir tizim roliga o'z-o'zidan tushmaydi va
// `Permission.DANGEROUS` ro'yxatida.
//
// ⚠️ Shuning uchun huquqi yo'q odamga bu blok umuman YUKLANMAYDI —
// so'rov ham ketmaydi. O'rniga sabab va yetishmayotgan huquq KODI
// yoziladi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'

const bankAccountSchema = z.object({
  id: uuidSchema,
  personId: uuidSchema,
  bankName: z.string(),
  accountNo: z.string(),
  /** `null` = hisob egasi xodimning o'zi */
  holderName: z.string().nullish(),
  /** `LocalDate` */
  validFrom: z.string(),
  /** `null` = JORIY */
  validTo: z.string().nullish(),
  open: z.boolean(),
  reason: z.string().nullish(),
  changedBy: z.string().nullish(),
  createdAt: instantSchema.nullish(),
})

export interface BankAccount {
  id: string
  personId: string
  bankName: string
  accountNo: string
  holderName: string | null
  validFrom: string
  validTo: string | null
  open: boolean
  reason: string | null
  changedBy: string | null
  createdAt: string | null
}

function toBankAccount(raw: z.infer<typeof bankAccountSchema>): BankAccount {
  return {
    id: raw.id,
    personId: raw.personId,
    bankName: raw.bankName,
    accountNo: raw.accountNo,
    holderName: raw.holderName ?? null,
    validFrom: raw.validFrom,
    validTo: raw.validTo ?? null,
    open: raw.open,
    reason: raw.reason ?? null,
    changedBy: raw.changedBy ?? null,
    createdAt: raw.createdAt ?? null,
  }
}

export function parseBankAccount(raw: unknown): BankAccount {
  return toBankAccount(bankAccountSchema.parse(raw))
}

/** Butun tarix — birinchi qator joriysi (`open === true`) */
export async function fetchBankAccounts(personId: string): Promise<readonly BankAccount[]> {
  const response = await api.get<unknown>(`/api/hr/persons/${personId}/bank-accounts`)
  return z.array(bankAccountSchema).parse(response.data).map(toBankAccount)
}

export interface BankAccountDraft {
  bankName: string
  /** ⚠️ 4..64 belgi. Shakl tekshiruvi YO'Q: IBAN, karta va ichki raqam ham qabul qilinadi */
  accountNo: string
  /** `null` = xodimning o'zi */
  holderName: string | null
  /** "YYYY-MM-DD" yoki `null` — server bugungi kunni qo'yadi */
  validFrom: string | null
  /** ⚠️ MAJBURIY */
  reason: string
}

export async function setBankAccount(
  personId: string,
  draft: BankAccountDraft,
): Promise<BankAccount> {
  const response = await api.post<unknown>(`/api/hr/persons/${personId}/bank-accounts`, draft)
  return parseBankAccount(response.data)
}

// --- Sof mantiq --------------------------------------------------------------

/**
 * Joriy rekvizit.
 *
 * ⚠️ Mezon — server aytgan `open`, «birinchi qator» EMAS. Tartib
 * o'zgarsa yoki kelajakdagi davr qo'shilsa, «birinchi qator» jimgina
 * noto'g'ri rekvizitni ko'rsatardi — va bu PUL ketadigan joy.
 */
export function currentAccount(accounts: readonly BankAccount[]): BankAccount | null {
  return accounts.find((account) => account.open) ?? null
}
